import { useState, useEffect, useMemo } from "react";
import { useQuery, ApolloError, useMutation, useSubscription } from "@apollo/client";
import {
    getEventReportsDetailsBO,
    getEventReportsDetailsTotalCountBO,
    getEventReportsIdsWithSecondaryCodesBOLive,
    getEventReportsWithSecondaryCodesBO,
    getEventReportsWithSecondaryCodesIdsBO,
    getFunctionalLocations,
    getLocationSpecificFailureCodes,
    getOperatorsForEventEdition,
    updateEventReportBO,
    getAreas,
    getFailureTypesOptions,
    getSecondaryFailureCodesOptions,
} from "../../api/queries/eventReportsAPI";
import {
    IArea,
    IEventReportSecondaryCodes,
    IEventReportDetails,
    IEventReportsEntity,
    IFailureCodeOptions,
    IFailureTypeOptions,
    IFunctionalLocation,
    ILocationSpecificFailureCodes,
    INonLocationSpecificFailureCodes,
    IOperator,
    ITableOperationFilters,
} from "./EventReportsEntityInterfaces";
import { END_OF_SHIFT_FAILURE_CODE_NAME, MEASURE_METRICS, allElements } from "constants/constants";
import moment from "moment";
import { IDetailsReportFilters } from "pages/Corporate/Locations/MillDashboards/DetailsReport/DetailsReportFilters/DetailsFiltersInterfaces";
import { getEndOfDateRange, getStartOfDateRange } from "utilities/utilities";
import { DetailsReportColumnsIdEnum } from "pages/Corporate/Locations/MillDashboards/DetailsReport/hooks/useDetailsReportColumnsInterfaces";
import { SecondaryCodesTableColumnsIdEnum } from "pages/Corporate/Locations/MillDashboards/SecondaryCodeRequirements/hooks/useSecondaryCodeRequirementsColumnsInterface";

export const EventReportsEntity: IEventReportsEntity = {
    GetPendingEventReportsWithSecondaryCodesBOLive: (millId: number) => {
        const { loading, error, data } = useSubscription(getEventReportsIdsWithSecondaryCodesBOLive, {
            fetchPolicy: "no-cache",
            variables: {
                where: {
                    secondary_failure_code_id: { _is_null: true },
                    measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                    ignore_event: { _eq: false },
                    start_date: { _gte: moment().subtract(5, "days").startOf("day") },
                    machine_center: { mill_id: { _eq: millId } },
                    secondary_code_valid: { _eq: true },
                    event_report_downtime_details: {},
                },
            },
        });
        const [pendingEventReportsQuantity, setPendingEventReportsQuantity] = useState<number>(0);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);
        useEffect(() => {
            if (data) {
                const eventReportsArrayQuantity: number = data.event_report_aggregate.aggregate.count;
                setPendingEventReportsQuantity(eventReportsArrayQuantity);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, pendingEventReportsQuantity, apolloQueryError };
    },
    GetEventReportsWithSecondaryCodesBO: (
        onlyPendingSecondaryCodeAndLocations: boolean,
        millId: number,
        tableOperationFilters: ITableOperationFilters,
    ) => {
        const [eventReports, setEventReports] = useState<IEventReportSecondaryCodes[]>([]);
        const [eventsIds, setEventsIds] = useState<number[]>([]);
        const [totalCount, setTotalCount] = useState<number>(0);

        const overallWhere = useMemo(() => {
            return {
                ...(onlyPendingSecondaryCodeAndLocations
                    ? {
                          secondary_failure_code_id: { _is_null: true },
                      }
                    : {}),
                measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                ignore_event: { _eq: false },
                start_date: { _gte: moment().subtract(5, "days").startOf("day") },
                machine_center: { mill_id: { _eq: millId } },
                secondary_code_valid: { _eq: true },
                event_report_downtime_details: {},
            };
        }, [onlyPendingSecondaryCodeAndLocations, millId]);

        const orderBy = useMemo(
            () => ({
                ...(!Boolean(tableOperationFilters.orderBy) && { start_date: "desc" }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.TIME_STOP && {
                    start_date: tableOperationFilters.order,
                }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.TIME_RESTART && {
                    end_date: tableOperationFilters.order,
                }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.MACHINE_CENTER && {
                    machine_center: { short_name: tableOperationFilters.order },
                }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.FUNCTIONAL_LOCATION && {
                    functional_location: { name: tableOperationFilters.order },
                }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.FAILURE_TYPE && {
                    failure_type: { name: tableOperationFilters.order },
                }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.FAILURE_CODE && {
                    failure_code: { name: tableOperationFilters.order },
                }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.SECONDARY_FAILURE_CODE && {
                    secondary_failure_code: { name: tableOperationFilters.order },
                }),
                ...(tableOperationFilters.orderBy === SecondaryCodesTableColumnsIdEnum.AREA && {
                    area: { name: tableOperationFilters.order },
                }),
            }),
            [tableOperationFilters],
        );

        const {
            refetch: refetchCount,
            error: errorCount,
            loading: loadingCount,
        } = useQuery(getEventReportsWithSecondaryCodesIdsBO, {
            fetchPolicy: "network-only",
            variables: {
                whereEvents: overallWhere,
            },
            onCompleted: (data) => {
                if (data?.event_report) {
                    const eventsData: {
                        event_report_id: number;
                    }[] = data?.event_report;
                    const count: number = data?.event_report?.length;
                    setEventsIds(eventsData.map((event) => event.event_report_id));
                    setTotalCount(count);
                    if (!eventsData.length) {
                        setEventReports([]);
                    }
                }
            },
        });

        const {
            loading: loadingDetails,
            error: errorDetails,
            refetch: refetchDetails,
        } = useQuery(getEventReportsWithSecondaryCodesBO, {
            fetchPolicy: "network-only",
            skip: !eventsIds.length,
            variables: {
                limit: tableOperationFilters.limit,
                offset: tableOperationFilters.offset,
                order_by: orderBy,
                whereEvents: { event_report_id: { _in: eventsIds } },
            },
            onCompleted: (data) => {
                if (data?.event_report) {
                    const eventReportsArray: IEventReportSecondaryCodes[] = data.event_report;
                    setEventReports(eventReportsArray);
                }
            },
        });

        const refetch = async () => {
            await refetchCount();
            await refetchDetails();
        };

        return {
            loading: loadingCount || loadingDetails,
            eventReports,
            totalCount,
            apolloQueryError: errorCount || errorDetails,
            refetch,
        };
    },
    GetLocationSpecificFailureCodesForSecondaryCodesEdition: (
        machineCenterId: number,
        functionalLocationId?: number,
    ) => {
        const [locationSpecificFailureCodes, setLocationSpecificFailureCodes] = useState<
            ILocationSpecificFailureCodes[]
        >([]);
        const [nonLocationSpecificFailureCodes, setNonLocationSpecificFailureCodes] = useState<
            INonLocationSpecificFailureCodes[]
        >([]);

        const { loading } = useQuery(getLocationSpecificFailureCodes, {
            skip: !machineCenterId,
            variables: {
                functionalLocationId: functionalLocationId,
                machineCenterId: machineCenterId,
                isLocationSpecific: !!functionalLocationId,
            },
            onCompleted: (data) => {
                if (data?.location_specific_functional_location_failure_code) {
                    const locationSpecificFailureCodes: ILocationSpecificFailureCodes[] =
                        data.location_specific_functional_location_failure_code;
                    setLocationSpecificFailureCodes(locationSpecificFailureCodes);
                } else {
                    setLocationSpecificFailureCodes([]);
                }
                if (data?.non_location_specific_failure_codes) {
                    const nonLocationSpecificFailureCodes: INonLocationSpecificFailureCodes[] =
                        data.non_location_specific_failure_codes;
                    setNonLocationSpecificFailureCodes(nonLocationSpecificFailureCodes);
                }
            },
        });

        return { loading, locationSpecificFailureCodes, nonLocationSpecificFailureCodes };
    },
    GetSecondaryFailureCodesForEdition: (
        machineCenterId: number,
        failureCodeId?: number,
        functionalLocationId?: number,
    ) => {
        const [secondaryFailureCodes, setSecondaryFailureCodes] = useState<IFailureCodeOptions[]>([]);

        const whereVariable = useMemo(
            () => ({
                primary_code_id: { _eq: failureCodeId },
                ...(functionalLocationId
                    ? {
                          location_specific_functional_location_failure_codes: {
                              functional_location_id: { _eq: functionalLocationId },
                          },
                      }
                    : {
                          non_location_specific_machine_center_failure_codes: {
                              machine_center_id: { _eq: machineCenterId },
                          },
                      }),
            }),
            [failureCodeId, functionalLocationId, machineCenterId],
        );

        const { loading } = useQuery(getSecondaryFailureCodesOptions, {
            skip: !failureCodeId,
            variables: {
                where: whereVariable,
            },
            onCompleted: (data) => {
                if (data?.failure_code) {
                    const failureCodesData: IFailureCodeOptions[] = data.failure_code;
                    setSecondaryFailureCodes(failureCodesData);
                }
            },
        });

        return { loading, secondaryFailureCodes };
    },
    GetFailureTypesOptionsForEdition: (machineCenterId: number) => {
        const [failureTypes, setFailureTypes] = useState<IFailureTypeOptions[]>([]);
        const { loading } = useQuery(getFailureTypesOptions, {
            variables: {
                machineCenterId: machineCenterId,
            },
            onCompleted: (data) => {
                if (data?.failure_type) {
                    const failureTypesData: IFailureTypeOptions[] = data.failure_type;
                    setFailureTypes(failureTypesData);
                }
            },
        });

        return { loading, failureTypes };
    },
    GetEventReportsDetailsBO: (
        millId: number,
        filters: IDetailsReportFilters,
        tableOperationFilters: ITableOperationFilters,
    ) => {
        const startDateRange = getStartOfDateRange(filters.startDate as Date);
        const endDateRange = getEndOfDateRange(filters.endDate as Date);

        const [eventReportsDetails, setEventReportsDetails] = useState<IEventReportDetails[]>([]);
        const [totalCount, setTotalCount] = useState<number>(0);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        const whereVariableDowntimeDetails = useMemo(
            () => ({
                _or: [
                    {
                        shift_date: {
                            _gte: startDateRange,
                            _lte: endDateRange,
                        },
                    },
                    {
                        end_date: { _is_null: true },
                        shift_date: { _lte: endDateRange },
                    },
                ],
                ...(filters.shift !== allElements && {
                    shift_id: { _eq: filters.shift },
                }),
            }),
            [filters, endDateRange, startDateRange],
        );

        const whereVariable = useMemo(
            () => ({
                _or: [
                    {
                        failure_code: {
                            _or: [
                                {
                                    code: { _neq: END_OF_SHIFT_FAILURE_CODE_NAME },
                                },
                                {
                                    code: { _is_null: true },
                                },
                            ],
                        },
                    },
                    {
                        failure_code_id: { _is_null: true },
                    },
                ],
                event_report_downtime_details: whereVariableDowntimeDetails,
                ...(filters.functionalLocation !== allElements && {
                    functional_location_id: {
                        _eq: filters.functionalLocation,
                    },
                }),
                ...(filters.failureTypes?.length! > 0 &&
                    !filters.failureTypes?.includes(allElements) && {
                        failure_type_id: {
                            _in: filters.failureTypes,
                        },
                    }),
                ...(filters.failureCodes?.length! > 0 &&
                    !filters.failureCodes?.includes(allElements) && {
                        failure_code_id: {
                            _in: filters.failureCodes,
                        },
                    }),
                ...(filters.operator !== allElements && {
                    user_id: {
                        _eq: filters.operator,
                    },
                }),
                machine_center: {
                    ...(filters.machineCenter?.length! > 0 &&
                        !filters.machineCenter?.includes(allElements) && {
                            machine_center_id: {
                                _in: filters.machineCenter,
                            },
                        }),
                    production_line: {
                        ...(filters.productionLine?.length! > 0 &&
                            !filters.productionLine?.includes(allElements) && {
                                production_line_id: {
                                    _in: filters.productionLine,
                                },
                            }),
                        mill_id: { _eq: millId },
                    },
                },
                measure_metric_id: { _eq: MEASURE_METRICS.DOWNTIME },
                ignore_event: { _eq: false },
            }),
            [filters, whereVariableDowntimeDetails, millId],
        );

        useQuery(getEventReportsDetailsTotalCountBO, {
            fetchPolicy: "no-cache",
            onCompleted: (data) => {
                if (data?.total_count) {
                    const count: number = data.total_count.aggregate.count;
                    setTotalCount(count);
                    if (!count) {
                        setEventReportsDetails([]);
                    }
                }
            },
            variables: {
                where: whereVariable,
            },
        });

        const { loading, error, refetch } = useQuery(getEventReportsDetailsBO, {
            skip: !totalCount,
            onCompleted: (data) => {
                if (data?.event_report) {
                    const eventReportsArray: IEventReportDetails[] = data.event_report;
                    setEventReportsDetails(eventReportsArray);
                }
            },
            variables: {
                limit: tableOperationFilters.limit,
                offset: tableOperationFilters.offset,
                order_by: {
                    ...(!Boolean(tableOperationFilters.orderBy) && { start_date: "desc" }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.DATE_STOP && {
                        start_date: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.SHIFT && {
                        shift_day: { shift: { name: tableOperationFilters.order } },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.PRODUCTION_LINE && {
                        machine_center: { production_line: { name: tableOperationFilters.order } },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.MACHINE_CENTER && {
                        machine_center: { name: tableOperationFilters.order },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.AREA && {
                        area: { name: tableOperationFilters.order },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.FUNCTIONAL_LOCATION && {
                        functional_location: { name: tableOperationFilters.order },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.FAILURE_TYPE && {
                        failure_type: { name: tableOperationFilters.order },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.FAILURE_CODE && {
                        failure_code: { name: tableOperationFilters.order },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.OPERATOR && {
                        user_operator: { first_name: tableOperationFilters.order },
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.COMMENTS && {
                        notes: tableOperationFilters.order,
                    }),
                },
                where: whereVariable,
                whereDowntimeDetails: whereVariableDowntimeDetails,
                order_by_downtime_details: {
                    ...(!Boolean(tableOperationFilters.orderBy) && { start_date: "desc" }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.DATE_STOP && {
                        start_date: tableOperationFilters.order,
                    }),
                    ...(tableOperationFilters.orderBy === DetailsReportColumnsIdEnum.SHIFT && {
                        shift: { name: tableOperationFilters.order },
                    }),
                },
            },
        });

        useEffect(() => {
            if (error) {
                setApolloQueryError(error);
            }
        }, [error]);

        return { loading, eventReportsDetails, totalCount, apolloQueryError, refetch };
    },
    GetFunctionalLocations: (areaId?: number, failureTypeId?: number) => {
        const [functionalLocations, setFunctionalLocations] = useState<IFunctionalLocation[]>([]);

        const { loading } = useQuery(getFunctionalLocations, {
            skip: !areaId || !failureTypeId,
            variables: {
                areaId,
                failureTypeId,
            },
            onCompleted: (data) => {
                if (data?.functional_location) {
                    const fucntionalLocationsData: IFunctionalLocation[] = data.functional_location;
                    setFunctionalLocations(fucntionalLocationsData);
                }
            },
        });

        return { loading, functionalLocations };
    },
    GetOperatorsForEventEdition: (machineCenterId: number, clusterId?: number | null) => {
        const [operators, setOperators] = useState<IOperator[]>([]);

        const { loading } = useQuery(getOperatorsForEventEdition, {
            skip: !machineCenterId,
            variables: {
                machineCenterId,
                clusterId,
                isCluster: !!clusterId,
            },
            onCompleted: (data) => {
                if (data?.user_machine_center) {
                    const operatorsData: IOperator[] = data.user_machine_center;
                    setOperators(operatorsData);
                }
                if (data?.user_cluster) {
                    const operatorsData: IOperator[] = data.user_cluster;
                    setOperators(operatorsData);
                }
            },
        });

        return { loading, operators };
    },
    GetAreas: (machineCenterId: number, failureTypeId?: number) => {
        const [areas, setAreas] = useState<IArea[]>([]);

        const { loading } = useQuery(getAreas, {
            skip: !failureTypeId,
            variables: {
                machineCenterId,
                failureTypeId,
            },
            onCompleted: (data) => {
                if (data?.area) {
                    const areaData: IArea[] = data.area;
                    setAreas(areaData);
                }
            },
        });

        return { loading, areas };
    },
    UpdateEventReportBO: () => {
        const [updateEventReportMutation, { error, loading }] = useMutation(updateEventReportBO);

        return { error, updateEventReportMutation, loading };
    },
};
